"use client";

import { useEffect, useSyncExternalStore } from "react";
import { Icon } from "@/components/ui/Icon";
import { THEME_STORAGE_KEY, type Theme } from "@/lib/theme";

/**
 * Das gewählte Theme lebt im DOM (`data-theme` am <html>), gesetzt vom
 * Init-Skript noch vor dem ersten Paint. `useSyncExternalStore` ist die
 * passende API, um diesen externen Zustand zu lesen: Der Server rendert
 * "light", nach der Hydration wird der echte Wert übernommen – ohne
 * setState im Effect und ohne Hydration-Warnung.
 */
function subscribe(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => observer.disconnect();
}

function getSnapshot(): Theme {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

/** Serverseitig ist die Wahl nicht bekannt – der Standard ist hell. */
function getServerSnapshot(): Theme {
  return "light";
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme);
}

export function ThemeToggle({ className = "" }: { className?: string }) {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Systemwechsel übernehmen, solange nichts explizit gewählt wurde.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem(THEME_STORAGE_KEY)) return;
      applyTheme(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  function toggle() {
    const next: Theme = getSnapshot() === "dark" ? "light" : "dark";
    applyTheme(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Privater Modus o. Ä. – die Wahl gilt dann nur für diese Sitzung.
    }
  }

  const isDark = theme === "dark";
  const label = isDark ? "Zu hellem Design wechseln" : "Zu dunklem Design wechseln";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className={`grid h-11 w-11 place-items-center rounded-full border border-line lg:h-9 lg:w-9 text-fg-muted transition-colors hover:border-line-strong hover:text-fg ${className}`}
    >
      <Icon name={isDark ? "sun" : "moon"} size={17} />
    </button>
  );
}
